import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { monitoredIdentityService, ApiError, MonitoredIdentityDto, UpdateMonitoredIdentityDto, CreateMonitoredIdentityDto, IdType } from '../services';
import { formatNationalId } from '../utils/nationalIdFormatter';
import { mapBackendErrorToTranslation } from '../utils/errorMapping';
import AddMonitoredIdentityForm from '../components/AddMonitoredIdentityForm';
import EditMonitoredIdentityForm from '../components/EditMonitoredIdentityForm';
import DisplayName from '../components/DisplayName';
import InlineLabelEditor from '../components/InlineLabelEditor';

interface IdentityRowProps {
  identity: MonitoredIdentityDto;
  onEdit: (identity: MonitoredIdentityDto) => void;
  onDelete: (identity: MonitoredIdentityDto) => void;
  onLabelUpdate: (id: string, label: string | null) => Promise<void>;
  editButtonRef: (el: HTMLElement | null) => void;
  deleteButtonRef: (el: HTMLElement | null) => void;
  getIdentityDisplayName: (identity: MonitoredIdentityDto) => string;
  t: (key: string, options?: any) => string;
  i18nLanguage: string;
  isMobile: boolean;
}

const IdentityRow = memo<IdentityRowProps>(({ 
  identity, 
  onEdit, 
  onDelete,
  onLabelUpdate,
  editButtonRef, 
  deleteButtonRef, 
  getIdentityDisplayName, 
  t, 
  i18nLanguage,
  isMobile
}) => {
  const formattedDate = useMemo(() => {
    return new Date(identity.createdAt).toLocaleDateString(i18nLanguage, {
      year: 'numeric',
      month: 'short', 
      day: 'numeric'
    });
  }, [identity.createdAt, i18nLanguage]);

  const handleEdit = useCallback(() => {
    onEdit(identity);
  }, [onEdit, identity]);

  const handleDelete = useCallback(() => {
    onDelete(identity);
  }, [onDelete, identity]);

  return (
    <Box sx={{ 
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : { 
        xs: '1fr', 
        sm: '48px 2fr 1fr 1fr 1fr 120px',
        md: '48px 2.5fr 1fr 1fr 1fr 120px'
      },
      gap: { xs: 2, sm: 2 },
      alignItems: 'center',
      px: 3,
      py: 2.5,
      minHeight: 80,
      borderBottom: '1px solid',
      borderColor: 'divider',
      '&:hover': {
        backgroundColor: 'action.hover'
      },
      '&:last-child': {
        borderBottom: 'none'
      },
      '@media (max-width: 320px)': {
        px: 2,
        py: 2,
        gap: 1.5,
        minHeight: 70,
        gridTemplateColumns: '1fr',
        '& > *': {
          textAlign: 'left',
          overflow: 'hidden'
        }
      }
    }}>
      {/* Column 1: Status Icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {identity.nationalId?.idType === IdType.PersonalId ? (
          <PersonIcon sx={{ 
            fontSize: 32,
            color: identity.isActive ? 'primary.main' : 'action.disabled'
          }} />
        ) : (
          <BusinessIcon sx={{ 
            fontSize: 32,
            color: identity.isActive ? 'primary.main' : 'action.disabled'
          }} />
        )}
      </Box>
      
      {/* Column 2: Name with status indicator and label editor */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        minWidth: 0,
        flexWrap: 'wrap'
      }}>
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            fontWeight: 600, 
            fontSize: '1rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          <DisplayName 
            identity={identity}
            variant="compact"
            showIdType={false}
          />
        </Typography>
        <InlineLabelEditor
          initialLabel={identity.label}
          onLabelSave={(label) => onLabelUpdate(identity.id, label)}
          placeholder={t('identities.addLabel')}
        />
        {!identity.isActive && (
          <Chip 
            label={t('identities.inactive')} 
            size="small" 
            color="default"
            sx={{ fontSize: '0.75rem', flexShrink: 0 }}
          />
        )}
      </Box>

      {/* Column 3: ID Type */}
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          fontSize: '0.875rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {identity.nationalId?.idType === IdType.PersonalId ? t('registration.personalId') : t('registration.companyId')}
      </Typography>

      {/* Column 4: National ID Number */}
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          fontSize: '0.875rem',
          fontFamily: 'monospace',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {identity.nationalId ? formatNationalId(
          identity.nationalId.normalizedId || '', 
          identity.nationalId.idType || IdType.PersonalId
        ) : '--'}
      </Typography>

      {/* Column 5: Creation date */}
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {formattedDate}
      </Typography>

      {/* Column 6: Actions */}
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        justifyContent: 'flex-end',
        alignItems: 'center'
      }}>
        <IconButton
          ref={editButtonRef}
          onClick={handleEdit}
          aria-label={t('identities.edit') + ' ' + getIdentityDisplayName(identity)}
          size="small"
          title={t('identities.edit')}
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'primary.action'
            }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          ref={deleteButtonRef}
          onClick={handleDelete}
          aria-label={t('identities.delete') + ' ' + getIdentityDisplayName(identity)}
          size="small"
          color="error"
          title={t('identities.delete')}
          sx={{ 
            '&:hover': {
              backgroundColor: 'error.action'
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
});

IdentityRow.displayName = 'IdentityRow';

const MonitoredIdentities: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showViewMentionsButton, setShowViewMentionsButton] = useState(false);
  const [identities, setIdentities] = useState<MonitoredIdentityDto[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingIdentity, setEditingIdentity] = useState<MonitoredIdentityDto | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [identityToDelete, setIdentityToDelete] = useState<MonitoredIdentityDto | null>(null);
  const [initialFormData, setInitialFormData] = useState<CreateMonitoredIdentityDto | null>(null);
  
  // Refs for focus management
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const editButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const deleteButtonRefs = useRef<Record<string, HTMLElement | null>>({});
  const fabRef = useRef<HTMLButtonElement>(null);
  
  // Screen reader announcements
  const [announcement, setAnnouncement] = useState<string>('');

  const loadErrorMessage = useMemo(() => t('messages.loadError'), [t]);

  const loadIdentities = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    setErrorMessage('');
    try {
      const identitiesData = await monitoredIdentityService.getAll(token);
      setIdentities(identitiesData);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(mapBackendErrorToTranslation(error.message, t));
      } else {
        setErrorMessage(loadErrorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, loadErrorMessage, t]);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  // Handle URL parameters for pre-populating the form
  useEffect(() => {
    // Only handle URL parameters if user is authenticated
    if (!token) return;
    
    const nationalId = searchParams.get('nationalId');
    const idType = searchParams.get('idType');
    
    if (nationalId && idType) {
      // Validate idType parameter - handle both string names and numeric values
      let validIdType: IdType | null = null;
      
      if (idType === 'PersonalId' || idType === '0') {
        validIdType = IdType.PersonalId;
      } else if (idType === 'CompanyId' || idType === '1') {
        validIdType = IdType.CompanyId;
      }
      
      console.log('Parsed IdType:', { original: idType, parsed: validIdType }); // Debug log
      
      if (validIdType !== null) {
        const formData = {
          nationalId: nationalId,
          idType: validIdType
        };
        console.log('Setting initial form data:', formData); // Debug log
        
        setInitialFormData(formData);
        console.log('Opening form with showAddForm=true'); // Debug log
        setShowAddForm(true);
        
        // Don't clear URL parameters immediately - let the form handle it
        // setSearchParams({});
      } else {
        console.warn('Invalid idType parameter:', idType);
      }
    }
  }, [searchParams, setSearchParams, token]);

  const handleAddIdentity = async (identityData: CreateMonitoredIdentityDto) => {
    if (!token) return;

    try {
      const newIdentity = await monitoredIdentityService.create(token, identityData);
      
      // Use mention count from create response
      const mentionCount = newIdentity.existingMentionCount || 0;
      
      if (mentionCount > 0) {
        const message = `${t('identities.addSuccess')} ${t('identities.existingMentionsFound', { count: mentionCount })}`;
        setSuccessMessage(message);
        setShowViewMentionsButton(true);
        setAnnouncement(message);
      } else {
        const message = t('identities.addSuccess');
        setSuccessMessage(message);
        setShowViewMentionsButton(false);
        setAnnouncement(message);
      }
      
      setShowAddForm(false);
      loadIdentities();
      
      // Return focus to the button that opened the form
      setTimeout(() => {
        if (addButtonRef.current) {
          addButtonRef.current.focus();
        } else if (fabRef.current) {
          fabRef.current.focus();
        }
      }, 100);
    } catch (error) {
      if (error instanceof ApiError) {
        // Check for timeout errors specifically
        if (error.message === 'timeout_error') {
          setErrorMessage(t('identities.addTimeoutError'));
        } else {
          // Map backend error to translated message
          setErrorMessage(mapBackendErrorToTranslation(error.message, t));
        }
      } else {
        setErrorMessage(t('identities.addError'));
      }
    }
  };

  const handleEditIdentity = (identity: MonitoredIdentityDto) => {
    setEditingIdentity(identity);
    setShowEditForm(true);
  };

  const handleUpdateIdentity = async (identityData: UpdateMonitoredIdentityDto) => {
    if (!token || !editingIdentity) return;

    try {
      await monitoredIdentityService.update(token, editingIdentity.id, identityData);
      const message = t('identities.updateSuccess');
      setSuccessMessage(message);
      setAnnouncement(message);
      setShowEditForm(false);
      const identityId = editingIdentity?.id;
      setEditingIdentity(null);
      loadIdentities();
      
      // Return focus to the edit button
      setTimeout(() => {
        if (identityId && editButtonRefs.current[identityId]) {
          editButtonRefs.current[identityId]?.focus();
        }
      }, 100);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(mapBackendErrorToTranslation(error.message, t));
      } else {
        setErrorMessage(t('identities.updateError'));
      }
    }
  };

  const handleDeleteClick = (identity: MonitoredIdentityDto) => {
    setIdentityToDelete(identity);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!token || !identityToDelete) return;

    setIsDeleting(true);
    try {
      await monitoredIdentityService.delete(token, identityToDelete.id);
      const message = t('identities.deleteSuccess');
      setSuccessMessage(message);
      setAnnouncement(message);
      setShowDeleteDialog(false);
      setIdentityToDelete(null);
      loadIdentities();
      
      // Focus should return to a safe location since the delete button no longer exists
      // Focus on the FAB or first identity's edit button
      setTimeout(() => {
        if (fabRef.current) {
          fabRef.current.focus();
        }
      }, 100);
    } catch (error) {
      if (error instanceof ApiError) {
        setErrorMessage(mapBackendErrorToTranslation(error.message, t));
      } else {
        setErrorMessage(t('identities.deleteError'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLabelUpdate = useCallback(async (id: string, label: string | null) => {
    if (!token) return;

    // Save the original label value before making changes
    const originalIdentity = identities.find(identity => identity.id === id);
    const originalLabel = originalIdentity?.label;

    // Update the local state optimistically
    setIdentities(prev => prev.map(identity => 
      identity.id === id 
        ? { ...identity, label: label || undefined } 
        : identity
    ));

    try {
      await monitoredIdentityService.updateLabel(token, id, label);
      
      const message = label 
        ? t('identities.labelUpdateSuccess')
        : t('identities.labelRemoveSuccess');
      setSuccessMessage(message);
      setAnnouncement(message);
    } catch (error) {
      // Revert the local state to the original value on API failure
      setIdentities(prev => prev.map(identity => 
        identity.id === id 
          ? { ...identity, label: originalLabel || undefined } 
          : identity
      ));

      if (error instanceof ApiError) {
        setErrorMessage(mapBackendErrorToTranslation(error.message, t));
      } else {
        setErrorMessage(t('identities.labelUpdateError'));
      }
      throw error; // Re-throw to let InlineLabelEditor handle the error state
    }
  }, [token, t, identities]);

  const getIdentityDisplayName = (identity: MonitoredIdentityDto) => {
    // This function is kept for backward compatibility with dialogs and accessibility labels
    if (identity.nationalId?.fullName) {
      return identity.nationalId.fullName;
    }
    
    if (identity.fullName) {
      return identity.fullName;
    }
    
    if (identity.nationalId) {
      const parts = [
        identity.nationalId.firstName,
        identity.nationalId.middleNames,
        identity.nationalId.lastName1,
        identity.nationalId.lastName2
      ].filter(Boolean);
      
      return parts.length > 0 ? parts.join(' ') : identity.nationalId.normalizedId || identity.nationalIdId;
    }
    
    return identity.nationalIdId || '--';
  };



  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Screen reader announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {announcement}
      </Box>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('identities.title')}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          {t('identities.description')}
        </Typography>

        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }} 
            onClose={() => {
              setSuccessMessage('');
              setShowViewMentionsButton(false);
            }}
            action={
              showViewMentionsButton ? (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={() => {
                    setSuccessMessage('');
                    setShowViewMentionsButton(false);
                    const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
                    const mentionsRoute = t('routes.mentions');
                    navigate(`/${currentLanguage}${mentionsRoute}`);
                  }}
                >
                  {t('identities.viewMentions')}
                </Button>
              ) : undefined
            }
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}

        {identities.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" component="h2" color="text.secondary" gutterBottom>
                {t('identities.noIdentities')}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mb: 3 }}
                id="no-identities-description"
              >
                {t('identities.noIdentitiesDescription')}
              </Typography>
              <Button
                ref={addButtonRef}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                aria-describedby="no-identities-description"
              >
                {t('identities.addFirst')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card sx={{ 
              borderRadius: 2,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              {!isMobile && (
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: '48px 2.5fr 1fr 1fr 1fr 120px',
                  gap: 2,
                  alignItems: 'center',
                  px: 3,
                  py: 1.5,
                  backgroundColor: 'grey.100',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {/* Icon column header - empty */}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('common.name')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('common.type')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('identities.nationalId')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                    {t('common.created')}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textAlign: 'right' }}>
                    {t('common.actions')}
                  </Typography>
                </Box>
              )}
            
              {/* Table Body */}
              <Box sx={{ 
                display: 'grid',
                gap: 0,
                '& > :nth-of-type(odd)': {
                  backgroundColor: 'grey.50'
                },
                '& > :nth-of-type(even)': {
                  backgroundColor: 'background.paper'
                }
              }}>
                {identities.map((identity) => (
                  <IdentityRow
                    key={identity.id}
                    identity={identity}
                    onEdit={handleEditIdentity}
                    onDelete={handleDeleteClick}
                    onLabelUpdate={handleLabelUpdate}
                    editButtonRef={(el) => { editButtonRefs.current[identity.id] = el; }}
                    deleteButtonRef={(el) => { deleteButtonRefs.current[identity.id] = el; }}
                    getIdentityDisplayName={getIdentityDisplayName}
                    t={t}
                    i18nLanguage={i18n.language}
                    isMobile={isMobile}
                  />
                ))}
              </Box>
            </Card>

            {/* Add Button - Below table, right-aligned */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 2
            }}>
              <Button
                ref={fabRef}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddForm(true)}
                sx={{ borderRadius: 2 }}
                aria-label={t('identities.addNew')}
              >
                {t('identities.addNew')}
              </Button>
            </Box>
          </>
        )}

        {/* Add Identity Form Dialog */}
        <AddMonitoredIdentityForm
          open={showAddForm}
          onClose={() => {
            setShowAddForm(false);
            setInitialFormData(null);
            // Clear URL parameters when form is closed
            if (searchParams.has('nationalId') || searchParams.has('idType')) {
              setSearchParams({});
            }
          }}
          onSubmit={handleAddIdentity}
          initialData={initialFormData}
        />

        {/* Edit Identity Form Dialog */}
        <EditMonitoredIdentityForm
          open={showEditForm}
          onClose={() => {
            setShowEditForm(false);
            setEditingIdentity(null);
          }}
          onSubmit={handleUpdateIdentity}
          identity={editingIdentity}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle color="error">
            {t('identities.confirmDeleteTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('identities.confirmDeleteMessage', { 
                name: identityToDelete ? getIdentityDisplayName(identityToDelete) : '' 
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('messages.deleting')}
                </>
              ) : (
                t('identities.delete')
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default MonitoredIdentities;